import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Snackbar,
  Alert,
  TextField,
  Button,
  Stack,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonIcon from "@mui/icons-material/Person";
import api from "../config/api";
import { API } from "../config/apiConfig";
import AdminPageWrapper from "./components/AdminPageWrapper";

// Helper functions
function n(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
}
function fmtNumber(x) {
  return n(x).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return String(dateStr);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(dateStr) {
  if (!dateStr) return "—";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return String(dateStr);
  return dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return String(dateStr);
  return dt.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDateKey(dateStr) {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().split("T")[0]; // YYYY-MM-DD
}

function getWeekKey(dateStr) {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return "";
  const weekStart = new Date(dt);
  weekStart.setDate(dt.getDate() - dt.getDay()); // Sunday
  return weekStart.toISOString().split("T")[0];
}

function formatWeekLabel(weekKey) {
  if (!weekKey) return "";
  const dt = new Date(weekKey);
  const weekEnd = new Date(dt);
  weekEnd.setDate(dt.getDate() + 6);
  return `${formatDate(weekKey)} - ${formatDate(weekEnd.toISOString())}`;
}

function getUserDisplayName(user) {
  if (user.FirstName && user.LastName) {
    return `${user.FirstName} ${user.LastName}`;
  }
  if (user.FirstName) return user.FirstName;
  if (user.UserName) return user.UserName;
  if (user.Email) return user.Email;
  return "Unknown User";
}

// Get start of current month in YYYY-MM-DD format
function getStartOfCurrentMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return new Date(year, month, 1).toISOString().split("T")[0];
}

// Get today's date in YYYY-MM-DD format
function getToday() {
  return new Date().toISOString().split("T")[0];
}

const AdminTokenRewards = () => {
  const theme = useTheme();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState("grouped"); // 'grouped' or 'table'
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [expandedDays, setExpandedDays] = useState(new Set());

  const [searchTerm, setSearchTerm] = useState("");
  // Initialize with default dates: start of current month and today
  const [startDate, setStartDate] = useState(getStartOfCurrentMonth());
  const [endDate, setEndDate] = useState(getToday());

  const fetchRewards = React.useCallback(async () => {
    // Require search term - don't fetch without it
    if (!searchTerm || searchTerm.trim() === "") {
      setRewards([]);
      setHasSearched(false);
      return;
    }

    // Use date range (defaults to start of month to today)
    const effectiveStartDate = startDate || getStartOfCurrentMonth();
    const effectiveEndDate = endDate || getToday();

    setLoading(true);
    setErrorMsg("");
    setHasSearched(true);
    try {
      const params = {};
      
      // Always include start date (start of current month by default)
      params.startDate = new Date(effectiveStartDate).toISOString();
      
      // Always include end date (today by default, set to end of day)
      const end = new Date(effectiveEndDate);
      end.setHours(23, 59, 59, 999);
      params.endDate = end.toISOString();
      
      // Search term is required
      params.searchTerm = searchTerm.trim();

      const res = await api.get(API.adminTokenLedger, { params });
      
      const data = res.data?.ledger || [];
      
      // Sort by date descending (newest first)
      const sorted = Array.isArray(data)
        ? data.sort((a, b) => {
            const dateA = new Date(a?.CreatedAt ?? 0);
            const dateB = new Date(b?.CreatedAt ?? 0);
            return dateB - dateA;
          })
        : [];
      setRewards(sorted);
    } catch (err) {
      console.error("❌ Failed to fetch token rewards:", err);
      setErrorMsg(err.response?.data?.error || "Failed to fetch token rewards");
      setRewards([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, searchTerm]);

  // Don't auto-fetch on mount - require search term first

  const handleSearch = () => {
    fetchRewards();
  };

  // Group rewards by user, then by week, then by day
  const groupedByUser = useMemo(() => {
    const userMap = {};
    
    rewards.forEach((tx) => {
      const userId = tx.UserID;
      if (!userId) return;

      if (!userMap[userId]) {
        userMap[userId] = {
          user: {
            UserID: userId,
            UserName: tx.UserName,
            FirstName: tx.FirstName,
            LastName: tx.LastName,
            Email: tx.Email,
            MobileNumber: tx.MobileNumber,
          },
          transactions: [],
          totalTokens: 0,
        };
      }

      userMap[userId].transactions.push(tx);
      userMap[userId].totalTokens += n(tx?.TokensGenerated ?? 0);
    });

    // Now group each user's transactions by week and day
    return Object.values(userMap).map((userData) => {
      const weekMap = {};
      
      userData.transactions.forEach((tx) => {
        const date = tx?.CreatedAt ?? "";
        const weekKey = getWeekKey(date);
        const dayKey = getDateKey(date);

        if (!weekKey || !dayKey) return;

        if (!weekMap[weekKey]) {
          weekMap[weekKey] = {};
        }
        if (!weekMap[weekKey][dayKey]) {
          weekMap[weekKey][dayKey] = [];
        }
        weekMap[weekKey][dayKey].push(tx);
      });

      // Convert to array structure
      const weeks = Object.keys(weekMap)
        .sort((a, b) => new Date(b) - new Date(a))
        .map((weekKey) => {
          const days = Object.keys(weekMap[weekKey])
            .sort((a, b) => new Date(b) - new Date(a))
            .map((dayKey) => ({
              dayKey,
              transactions: weekMap[weekKey][dayKey],
              dayTotal: weekMap[weekKey][dayKey].reduce(
                (sum, tx) => sum + n(tx?.TokensGenerated ?? 0),
                0
              ),
            }));

          const weekTransactions = Object.values(weekMap[weekKey]).flat();
          return {
            weekKey,
            days,
            weekTotal: weekTransactions.reduce(
              (sum, tx) => sum + n(tx?.TokensGenerated ?? 0),
              0
            ),
            weekCount: weekTransactions.length,
          };
        });

      return {
        ...userData,
        weeks,
      };
    });
  }, [rewards]);

  const toggleUser = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const toggleWeek = (weekKey) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(weekKey)) {
      newExpanded.delete(weekKey);
    } else {
      newExpanded.add(weekKey);
    }
    setExpandedWeeks(newExpanded);
  };

  const toggleDay = (dayKey) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(dayKey)) {
      newExpanded.delete(dayKey);
    } else {
      newExpanded.add(dayKey);
    }
    setExpandedDays(newExpanded);
  };

  return (
    <AdminPageWrapper
      title="Token Rewards"
      subtitle="Search and view token rewards by user"
    >
      {/* Header with View Toggle */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box />
        <Box display="flex" gap={1} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="grouped">
              <ViewModuleIcon sx={{ mr: 0.5 }} fontSize="small" />
              Grouped
            </ToggleButton>
            <ToggleButton value="table">
              <ViewListIcon sx={{ mr: 0.5 }} fontSize="small" />
              Table
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRewards} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* 🔍 Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search Users"
          placeholder="Search by name, email, mobile..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255,255,255,0.8)",
              backdropFilter: "blur(10px)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(255,255,255,0.2)",
              "&:hover": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(102, 126, 234, 0.5)",
              },
              "&.Mui-focused": {
                borderColor: "#4FACFE",
                boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
              },
            },
          })}
        />
        <TextField
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255,255,255,0.8)",
              backdropFilter: "blur(10px)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(255,255,255,0.2)",
            },
          })}
        />
        <TextField
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255,255,255,0.8)",
              backdropFilter: "blur(10px)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(255,255,255,0.2)",
            },
          })}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!searchTerm || searchTerm.trim() === ""}
          sx={{
            background: "linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
            },
            "&:disabled": {
              background: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
            transition: "all 0.3s ease",
            px: 3,
          }}
        >
          Search
        </Button>
      </Stack>

      {/* Loading */}
      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {!loading && errorMsg && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "error.light",
            borderRadius: 2,
            mb: 2,
            display: "flex",
            gap: 1,
            alignItems: "center",
            color: "error.main",
          }}
        >
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2">{errorMsg}</Typography>
        </Paper>
      )}

      {/* Empty State */}
      {!loading && !errorMsg && rewards.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {hasSearched
              ? "No token rewards found for the selected search and date range"
              : "Enter a search term to find token rewards"}
          </Typography>
        </Paper>
      )}

      {/* Grouped View - By User */}
      {!loading &&
        !errorMsg &&
        rewards.length > 0 &&
        viewMode === "grouped" && (
          <Stack spacing={3}>
            {groupedByUser.map((userData) => {
              const userId = userData.user.UserID;
              const isUserExpanded = expandedUsers.has(userId);
              const displayName = getUserDisplayName(userData.user);

              return (
                <Paper
                  key={userId}
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {/* User Summary Card */}
                  <Box
                    sx={{
                      p: 2,
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(102, 126, 234, 0.1)"
                          : "rgba(102, 126, 234, 0.05)",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <PersonIcon color="primary" />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {displayName}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {userData.user.Email || "—"}
                        </Typography>
                        {userData.user.MobileNumber && (
                          <Typography variant="body2" color="text.secondary">
                            {userData.user.MobileNumber}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Transactions
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {userData.transactions.length}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body2" color="text.secondary">
                              Total Tokens
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: "success.main" }}
                            >
                              +{fmtNumber(userData.totalTokens)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* User Header - Collapsible */}
                  <Box
                    onClick={() => toggleUser(userId)}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "action.hover",
                      "&:hover": { bgcolor: "action.selected" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {isUserExpanded ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {displayName}
                      </Typography>
                      <Chip
                        label={`${userData.transactions.length} ${
                          userData.transactions.length === 1
                            ? "transaction"
                            : "transactions"
                        }`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "success.main" }}
                    >
                      +{fmtNumber(userData.totalTokens)} tokens
                    </Typography>
                  </Box>

                  {/* User Transactions - Grouped by Week/Day */}
                  <Collapse in={isUserExpanded}>
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        {userData.weeks.map((week) => {
                          const weekKey = `${userId}-${week.weekKey}`;
                          const isWeekExpanded = expandedWeeks.has(weekKey);
                          return (
                            <Paper
                              key={weekKey}
                              elevation={0}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              {/* Week Header */}
                              <Box
                                onClick={() => toggleWeek(weekKey)}
                                sx={{
                                  p: 1.5,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  bgcolor: "action.hover",
                                  "&:hover": { bgcolor: "action.selected" },
                                  transition: "background-color 0.2s",
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  {isWeekExpanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {formatWeekLabel(week.weekKey)}
                                  </Typography>
                                  <Chip
                                    label={`${week.weekCount} ${
                                      week.weekCount === 1
                                        ? "transaction"
                                        : "transactions"
                                    }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600, color: "success.main" }}
                                >
                                  +{fmtNumber(week.weekTotal)} tokens
                                </Typography>
                              </Box>

                              {/* Week Content - Days */}
                              <Collapse in={isWeekExpanded}>
                                <Box sx={{ p: 1.5 }}>
                                  <Stack spacing={1}>
                                    {week.days.map((day) => {
                                      const dayKey = `${weekKey}-${day.dayKey}`;
                                      const isDayExpanded =
                                        expandedDays.has(dayKey);
                                      return (
                                        <Paper
                                          key={dayKey}
                                          elevation={0}
                                          sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            overflow: "hidden",
                                          }}
                                        >
                                          {/* Day Header */}
                                          <Box
                                            onClick={() => toggleDay(dayKey)}
                                            sx={{
                                              p: 1.5,
                                              cursor: "pointer",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              bgcolor: "background.default",
                                              "&:hover": { bgcolor: "action.hover" },
                                              transition: "background-color 0.2s",
                                            }}
                                          >
                                            <Box display="flex" alignItems="center" gap={1}>
                                              {isDayExpanded ? (
                                                <ExpandLessIcon fontSize="small" />
                                              ) : (
                                                <ExpandMoreIcon fontSize="small" />
                                              )}
                                              <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                {formatDate(day.dayKey)}
                                              </Typography>
                                              <Chip
                                                label={`${day.transactions.length} ${
                                                  day.transactions.length === 1
                                                    ? "tx"
                                                    : "txs"
                                                }`}
                                                size="small"
                                                variant="outlined"
                                              />
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                color: "success.main",
                                              }}
                                            >
                                              +{fmtNumber(day.dayTotal)} tokens
                                            </Typography>
                                          </Box>

                                          {/* Day Transactions */}
                                          <Collapse in={isDayExpanded}>
                                            <Box sx={{ p: 1, bgcolor: "background.paper" }}>
                                              <Stack spacing={0.5}>
                                                {day.transactions.map((tx, idx) => {
                                                  const tokens =
                                                    tx?.TokensGenerated ?? 0;
                                                  const source = tx?.Source ?? "Unknown";
                                                  const createdAt =
                                                    tx?.CreatedAt ?? "";
                                                  return (
                                                    <Box
                                                      key={idx}
                                                      sx={{
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        bgcolor: "action.hover",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                          "space-between",
                                                      }}
                                                    >
                                                      <Box>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ fontWeight: 600 }}
                                                        >
                                                          {fmtNumber(tokens)} tokens
                                                        </Typography>
                                                        <Typography
                                                          variant="caption"
                                                          color="text.secondary"
                                                        >
                                                          {source} • {formatTime(createdAt)}
                                                        </Typography>
                                                      </Box>
                                                      <Chip
                                                        label={source}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ fontSize: "0.7rem" }}
                                                      />
                                                    </Box>
                                                  );
                                                })}
                                              </Stack>
                                            </Box>
                                          </Collapse>
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        )}

      {/* Table View */}
      {!loading &&
        !errorMsg &&
        rewards.length > 0 &&
        viewMode === "table" && (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Tokens
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rewards.map((tx, idx) => {
                  const tokens = tx?.TokensGenerated ?? 0;
                  const source = tx?.Source ?? "Unknown";
                  const createdAt = tx?.CreatedAt ?? "";
                  const displayName = getUserDisplayName(tx);
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>{displayName}</TableCell>
                      <TableCell>{tx.Email || "—"}</TableCell>
                      <TableCell>{tx.MobileNumber || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={source}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        +{fmtNumber(tokens)}
                      </TableCell>
                      <TableCell>{formatDateTime(createdAt)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={4000}
        onClose={() => setErrorMsg("")}
      >
        <Alert
          onClose={() => setErrorMsg("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
};

export default AdminTokenRewards;
